'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, Search } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Music', href: '/music' },
  { label: 'Videos', href: '/videos' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Events', href: '/events' },
  { label: 'News', href: '/news' },
  { label: 'Press', href: '/press' },
  { label: 'Booking', href: '/booking' },
];

export default function PublicTopbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isHome = pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background/95 backdrop-blur-md border-b border-border' :'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={36} />
            <span className="font-sans text-lg font-700 tracking-tight text-gradient-gold hidden sm:block">
              HADATHA
            </span>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems?.map((item) => (
            <Link
              key={`nav-${item?.label}`}
              href={item?.href}
              className={`px-4 py-2 text-sm font-medium transition-colors duration-150 rounded-lg hover:bg-muted/40 ${
                pathname === item?.href
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {item?.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            href="/search"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150"
            aria-label="Search"
          >
            <Search size={18} />
          </Link>
          <LanguageSwitcher />
          <Link
            href="/booking"
            className="hidden sm:flex items-center px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-gold text-primary-foreground hover:opacity-90 active:scale-95 transition-all duration-150"
          >
            Book Artist
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-card border-b border-border animate-slide-up">
          <nav className="max-w-screen-2xl mx-auto px-6 py-4 flex flex-col gap-1">
            {navItems?.map((item) => (
              <Link
                key={`mobile-nav-${item?.label}`}
                href={item?.href}
                className={`px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                  pathname === item?.href
                    ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {item?.label}
              </Link>
            ))}
            <Link
              href="/booking"
              className="mt-2 px-4 py-3 text-sm font-semibold text-center rounded-lg bg-gradient-gold text-primary-foreground"
              onClick={() => setMenuOpen(false)}
            >
              Book Artist
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}